/**
 * AFG-1.0 — Ask vs Fair Value Gap.
 *
 * يفصل مفهومين كانا مختلطين، وهو الخلط الذي سبّب النص الخاطئ:
 *
 *   **range_position** — أين يقع السعر المطلوب من **حدّي** النطاق.
 *   **gap_pct_vs_point** — كم يبعد عن **التقدير المركزي** نسبةً.
 *
 * سعر 4,000 داخل نطاق 1,975–4,825 هو `WITHIN_FAIR_VALUE_RANGE`، حتى وإن كان
 * أعلى من المركز 3,400 بـ17.65%. قول «أعلى من النطاق بـ17.6%» خطأ مزدوج:
 * التصنيف خاطئ، والنسبة محسوبة مقابل مرجع آخر غير المذكور.
 *
 * ⛔ لا يغيّر FV ولا OP ولا أي عتبة — يصف موقع السعر فقط.
 */

export const AFG_MODEL_VERSION = 'AFG-1.0';

export type RangePosition =
  | 'BELOW_FAIR_VALUE_RANGE'
  | 'WITHIN_FAIR_VALUE_RANGE'
  | 'ABOVE_FAIR_VALUE_RANGE';

export interface AskFairValueGap {
  asking_price_per_sqm: number;
  fair_value_low: number;
  fair_value_point: number;
  fair_value_high: number;

  /** موقع السعر من حدّي النطاق — لا علاقة له بالنسبة أدناه. */
  range_position: RangePosition;
  /** الفارق عن التقدير المركزي (%). موجب = أعلى. */
  gap_pct_vs_point: number;
  /** الفارق عن الحد الأدنى (%). سالب = تحته. */
  gap_pct_vs_low: number;
  /** الفارق عن الحد الأعلى (%). موجب = فوقه. */
  gap_pct_vs_high: number;
  model_version: string;
}

export function computeAskFairValueGap(input: {
  asking_price_per_sqm: number;
  fair_value_low: number;
  fair_value_point: number;
  fair_value_high: number;
}): AskFairValueGap {
  const { asking_price_per_sqm: ask, fair_value_low: low, fair_value_point: point, fair_value_high: high } = input;

  // الحدود شاملة: السعر المطابق لحدّ النطاق يقع داخله.
  const range_position: RangePosition =
    ask < low ? 'BELOW_FAIR_VALUE_RANGE' : ask > high ? 'ABOVE_FAIR_VALUE_RANGE' : 'WITHIN_FAIR_VALUE_RANGE';

  const pct = (reference: number): number =>
    reference > 0 ? +(((ask - reference) / reference) * 100).toFixed(2) : 0;

  return {
    asking_price_per_sqm: ask,
    fair_value_low: low,
    fair_value_point: point,
    fair_value_high: high,
    range_position,
    gap_pct_vs_point: pct(point),
    gap_pct_vs_low: pct(low),
    gap_pct_vs_high: pct(high),
    model_version: AFG_MODEL_VERSION,
  };
}

/** وصف الموقع من النطاق — مرجعه الحدّان. */
export const RANGE_POSITION_PHRASE: Record<RangePosition, string> = {
  BELOW_FAIR_VALUE_RANGE: 'يقع أسفل نطاق القيمة العادلة',
  WITHIN_FAIR_VALUE_RANGE: 'يقع داخل نطاق القيمة العادلة',
  ABOVE_FAIR_VALUE_RANGE: 'يقع أعلى من نطاق القيمة العادلة',
};

/**
 * الجملة الكاملة: موقع من النطاق **ثم** فارق عن المركز — كل نسبة مقرونة
 * صراحةً بمرجعها حتى لا يُقرأ الرقم على غير أساسه.
 */
export function describeGap(gap: AskFairValueGap): string {
  const position = RANGE_POSITION_PHRASE[gap.range_position];
  const delta = Math.abs(gap.gap_pct_vs_point).toFixed(1);

  if (gap.gap_pct_vs_point === 0) {
    return `السعر المطلوب ${position}، وهو مطابق للتقدير المركزي.`;
  }
  const direction = gap.gap_pct_vs_point > 0 ? 'أعلى من' : 'أقل من';
  return `السعر المطلوب ${position}، وهو ${direction} التقدير المركزي بـ${delta}%.`;
}
